import { DotIndicator } from "./dot-indicator";
import {
  GALLERY_CHANGE_EVENT,
  GALLERY_PAYWALL_DIALOG_OPEN_EVENT,
} from "./events.js";
import { type ChangeEvent } from "./gallery";
import { InteractionObserver } from "./utils";

const template = document.createElement("template");

template.innerHTML = `
  <style>
    :host {
      display: block;
      position: relative;
    }

    :host([hidden]) {
      display: none;
    }

    .scroll-container {
      display: flex;
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
    }

    .scroll-container::-webkit-scrollbar {
      display: none;
    }

    .items::slotted(*) {
      flex: none;
      inline-size: 100%;
      scroll-snap-align: start;
    }

    .item-counter {
      backdrop-filter: blur(6px);
      background-color: var(--ws-gallery-lead-item-counter-background-color);
      border-radius: 4px;
      color: var(--ws-gallery-lead-item-counter-color);
      inset-block-start: var(--ws-gallery-lead-space-block);
      inset-inline-end: var(--ws-gallery-lead-space-inline);
      padding-block: var(--ws-gallery-lead-item-counter-padding-block);
      padding-inline: var(--ws-gallery-lead-item-counter-padding-inline);
      position: absolute;
      z-index: 2;
    }

    .item-counter[hidden] {
      display: none;
    }

    .current-item {
      color: var(--ws-gallery-lead-current-item-color);
      font-weight: var(--ws-gallery-lead-current-item-font-weight);
    }

    .navigation {
      aspect-ratio: 16 / 9;
      display: none;
      inset: 0 0 auto 0;
      pointer-events: none;
      position: absolute;
    }

    .navigation__buttons {
      display: flex;
      gap: 12px;
      inset: auto 24px 24px auto;
      position: absolute;
    }

    @media (min-width: 768px) {
      .navigation {
        display: block;
      }
    }

    @media (min-width: 1024px) {
      .navigation__buttons {
        inset: auto 40px 30px auto;
      }
    }

    @media (prefers-reduced-motion: no-preference) {
      :host {
        scroll-behavior: smooth;
      }
    }
  </style>
  <div class="scroll-container"><slot class="items"></slot></div>
  <div class="item-counter" part="item-counter"><span class="current-item"></span>/<span class="total-items"></span></div>
  <nav class="navigation">
    <div class="navigation__buttons" part="navigation">
      <slot name="previous"></slot>
      <slot name="next"></slot>
    </div>
  </nav>
  <div class="dots">
    <slot name="dots"></slot>
  </div>
  <div class="thumbnails">
    <slot name="thumbnails"></slot>
  </div>
</div>
`;

export class GalleryLead extends HTMLElement {
  #currentItem: HTMLElement;
  #currentItemNodeIndex = 0;
  #dots: DotIndicator;
  #interactionObserver?: InteractionObserver;
  #intersectionCounter = 0;
  #itemCounter: HTMLElement;
  #items: HTMLElement[];
  #itemsObserver: IntersectionObserver;
  #mutationObserver: MutationObserver;
  #nextButton: HTMLAnchorElement;
  #prevButton: HTMLAnchorElement;
  #resizeObserver: ResizeObserver;
  #scrollContainer: HTMLElement;
  #slots: Record<string, HTMLSlotElement> = {};
  #totalItems: HTMLElement;
  #touchStartX = 0;
  #touchStartY = 0;
  #thumbnails: HTMLElement;
  #thumbnailScroller: HTMLElement;
  #thumbnailItems: HTMLElement[];

  get loading() {
    return this.getAttribute("loading") ?? "eager";
  }

  get pageNumber() {
    return parseInt(this.getAttribute("page-number") ?? "0");
  }

  get totalItems() {
    return this.getAttribute("total-items") ?? "0";
  }

  get dataHeadline() {
    return this.getAttribute("data-headline") ?? "";
  }

  get dataTotalItems() {
    return this.getAttribute("data-total-items") ?? "";
  }

  get isRestricted() {
    return this.hasAttribute("data-is-restricted");
  }

  constructor() {
    super();

    // Setup Shadow DDOM
    this.attachShadow({ mode: "open" });
    this.shadowRoot!.appendChild(template.content.cloneNode(true));

    // Setup HTML elements
    this.#currentItem = this.shadowRoot!.querySelector(".current-item")!;
    this.#itemCounter = this.shadowRoot!.querySelector(".item-counter")!;
    this.#slots.items = this.shadowRoot!.querySelector(
      ".items",
    )! as HTMLSlotElement;
    this.#items = this.#slots.items.assignedElements()! as HTMLElement[];
    this.#slots.next = this.shadowRoot!.querySelector(
      `slot[name="next"]`,
    )! as HTMLSlotElement;
    this.#nextButton =
      this.#slots.next.assignedElements()[0]! as HTMLAnchorElement;
    this.#slots.previous = this.shadowRoot!.querySelector(
      `slot[name="previous"]`,
    )! as HTMLSlotElement;
    this.#prevButton =
      this.#slots.previous.assignedElements()[0]! as HTMLAnchorElement;
    this.#scrollContainer =
      this.shadowRoot!.querySelector(".scroll-container")!;
    this.#totalItems = this.shadowRoot!.querySelector(".total-items")!;
    this.#slots.dots = this.shadowRoot!.querySelector(
      `slot[name="dots"]`,
    )! as HTMLSlotElement;
    this.#dots = this.#slots.dots.assignedElements()[0]! as DotIndicator;
    this.#slots.thumbnails = this.shadowRoot!.querySelector(
      `slot[name="thumbnails"]`,
    )! as HTMLSlotElement;
    this.#thumbnails =
      this.#slots.thumbnails.assignedElements()[0]! as HTMLElement;
    this.#thumbnailScroller = this.#thumbnails.querySelector(
      ".group-teaser-gallery__scroller",
    )!;
    this.#thumbnailItems = Array.from(
      this.#thumbnails.querySelectorAll(".teaser"),
    );

    // Setup IntersectionObserver
    this.#itemsObserver = new IntersectionObserver(this.#handleIntersection, {
      root: this.#scrollContainer,
      rootMargin: "0px",
      threshold: 0.6,
    });

    // Setup InteractionObserver
    if (this.loading === "interaction") {
      this.#interactionObserver = new InteractionObserver(
        this.#handleInteraction,
      );
    }

    // Setup MutationObserver
    this.#mutationObserver = new MutationObserver(this.#handleMutation);

    //Setup ResizeObserver
    this.#resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
        this.#scrollContainer.scroll({
          behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
          left: entry.contentRect.width * this.#currentItemNodeIndex,
        });
      }
    });
  }

  connectedCallback() {
    // Setup item counter
    this.#totalItems.textContent = this.totalItems;

    // Setup IntersectionObserver for each item
    // Button visibility and item counter are updated on the initial intersection callback.
    for (const item of this.#items) {
      this.#itemsObserver.observe(item);
    }

    this.#resizeObserver.observe(this.#scrollContainer);

    // Setup MutationObserver to add dynamically loaded items to the IntersectionObserver
    this.#mutationObserver.observe(this, { childList: true });

    // Setup event listeners
    if (this.#nextButton && !this.isRestricted) {
      this.#nextButton.addEventListener("click", this.#scrollNext);
    }
    if (this.#prevButton && !this.isRestricted) {
      this.#prevButton.addEventListener("click", this.#scrollPrevious);
    }
    this.#thumbnailItems.forEach((item) => {
      item.addEventListener("click", this.#handleThumbnailClick);
    });

    this.#setInactiveSlideCaptionsInert(this.#currentItemNodeIndex);

    if (this.isRestricted) {
      this.#scrollContainer.addEventListener("wheel", this.#handleWheel);
      this.#scrollContainer.addEventListener(
        "touchstart",
        this.#handleTouchStart,
      );
      this.#scrollContainer.addEventListener(
        "touchmove",
        this.#handleTouchMove,
      );
    }

    this.loading === "interaction"
      ? this.#interactionObserver?.observe(document.body)
      : this.#handleInteraction();
  }

  getViewportElement() {
    return this.#scrollContainer;
  }

  #handleInteraction = () => {
    this.#interactionObserver?.disconnect();

    // Activate all items
    this.#items.forEach((item) => {
      item.removeAttribute("disabled");
    });
  };

  #handleWheel = (event: WheelEvent) => {
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      event.preventDefault();
      this.#dispatchOpenPaywallDialogEvent();
    }
  };

  #handleTouchStart = (event: TouchEvent) => {
    const touch = event.touches[0];
    this.#touchStartX = touch!.clientX;
    this.#touchStartY = touch!.clientY;
  };

  #handleTouchMove = (event: TouchEvent) => {
    const touch = event.touches[0];
    const touchEndX = touch!.clientX;
    const touchEndY = touch!.clientY;
    const deltaX = touchEndX - this.#touchStartX;
    const deltaY = touchEndY - this.#touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
      this.#dispatchOpenPaywallDialogEvent();
    }
  };

  // Triggers also on intersection of first gallery item (not only when navigating through the gallery)
  #handleIntersection = (entries: IntersectionObserverEntry[]) => {
    const galleryHeadline = this.dataHeadline;
    const totalItemsWithAds = this.dataTotalItems;

    for (const entry of entries) {
      if (entry.isIntersecting && entry.target instanceof HTMLElement) {
        //Set #currentItemNodeIndex to pin position in scroll container
        this.#currentItemNodeIndex = Array.prototype.indexOf.call(
          this.#slots.items!.assignedElements(),
          entry.target,
        );

        const itemIndex = entry.target.dataset.itemIndex; // actual slides incl. ads
        const itemNumber = entry.target.dataset.itemNumber; // counts only images
        let itemType = entry.target.dataset.itemType;
        // a requirement from the data team
        if (itemType === "image-element") itemType = "image";

        this.#updateItemCounter(itemNumber);
        this.#updateThumbnailClasses(itemNumber);
        this.#updateThumbnailPosition(itemNumber);
        this.#setInactiveSlideCaptionsInert(this.#currentItemNodeIndex);

        // Re-enable component
        const itemUrl = entry.target.dataset.itemUrl;
        const totalItems = this.totalItems;

        this.#updateBrowserUrl(itemUrl);
        this.#updateButtonsVisibility();

        if (this.#intersectionCounter > 0) {
          this.#updateItemClasses(itemIndex);
          this.#updateDotIndicator(itemIndex);
          this.#dispatchChangeEvent(
            this.#intersectionCounter,
            galleryHeadline,
            itemIndex,
            itemNumber,
            itemType,
            itemUrl,
            totalItems,
            totalItemsWithAds,
          );
        }
        this.#intersectionCounter++;
      }
    }
  };

  #handleMutation = (mutationList: MutationRecord[]) => {
    for (const mutation of mutationList) {
      if (mutation.type === "childList") {
        if (mutation.addedNodes.length > 0) {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.#itemsObserver.observe(node);
            }
          });
          // Deeplink: When loading the previous images, the gallery jumps to the first image.
          if (this.pageNumber > 1) {
            this.#scrollToSlide(this.pageNumber);
          }
        }
        if (mutation.removedNodes.length > 0) {
          mutation.removedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              this.#itemsObserver.unobserve(node);
            }
          });
        }
        this.#updateButtonsVisibility();
        this.#setInactiveSlideCaptionsInert(this.#currentItemNodeIndex);
      }
    }
  };

  #dispatchChangeEvent = (
    count: number,
    galleryHeadline?: string,
    itemIndex?: string,
    itemNumber?: string,
    itemType?: string,
    itemUrl?: string,
    totalItems?: string,
    totalItemsWithAds?: string,
  ) => {
    const event: ChangeEvent = new CustomEvent(GALLERY_CHANGE_EVENT, {
      detail: {
        count,
        galleryHeadline,
        itemIndex,
        itemNumber,
        itemType,
        itemUrl,
        totalItems,
        totalItemsWithAds,
        adTagIds: [
          "skyscraper_1",
          "skyscraper_2",
          "skyscraper_3",
          "skyscraper_4",
          "superbanner_1",
          "superbanner_3",
          "superbanner_6",
          "wallpaper_1",
        ],
      },
    });

    this.dispatchEvent(event);
  };

  #dispatchOpenPaywallDialogEvent = () => {
    document.dispatchEvent(new CustomEvent(GALLERY_PAYWALL_DIALOG_OPEN_EVENT));
  };

  // Hide off-screen slides from sequential focus navigation
  #setInactiveSlideCaptionsInert(activeIndex: number) {
    const items = this.#slots.items!.assignedElements()! as HTMLElement[];
    if (!items || items.length === 0) return;
    items.forEach((item, index) => {
      const isActive = index === activeIndex;
      item.toggleAttribute("inert", !isActive);
      item.setAttribute("aria-hidden", (!isActive).toString());
    });
  }

  #scrollNext = (event: MouseEvent) => {
    event.preventDefault();
    // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
    this.#scrollContainer.scroll({
      behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
      left:
        this.#scrollContainer.scrollLeft + this.#scrollContainer.clientWidth,
    });
  };

  #scrollPrevious = (event: MouseEvent) => {
    event.preventDefault();
    // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
    this.#scrollContainer.scroll({
      behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
      left:
        this.#scrollContainer.scrollLeft - this.#scrollContainer.clientWidth,
    });
  };

  #scrollToSlide = (slideNumber: number) => {
    if (!slideNumber) return;
    const adCount = slideNumber > 3 ? Math.ceil((slideNumber - 3) / 3) : 0;
    // Do not use scrollBy, it has issues with zoom on Safari 18.0.1
    this.#scrollContainer.scroll({
      behavior: "instant", // Prevent scrolling issues in combination with css scroll-snap-type
      left: this.#scrollContainer.clientWidth * (slideNumber + adCount - 1),
    });
  };

  #handleThumbnailClick = (event: MouseEvent) => {
    event.preventDefault();
    const thumb = (event.target as Element).closest(
      ".group-teaser-gallery__item",
    ) as unknown as HTMLElement;
    if (
      thumb &&
      !thumb.classList.contains("active") &&
      thumb.dataset.imageNumber
    ) {
      if (this.isRestricted) {
        this.#dispatchOpenPaywallDialogEvent();
      } else {
        this.#scrollToSlide(parseInt(thumb.dataset.imageNumber));
      }
    }
  };

  #updateButtonsVisibility() {
    if (this.isRestricted) {
      const totalItemsCount = parseInt(this.totalItems, 10);
      const currentItemNumber = Number(this.#currentItem.textContent);

      if (totalItemsCount <= 1) {
        this.#nextButton.inert = true;
        this.#prevButton.inert = true;
      } else if (totalItemsCount > 1) {
        this.#nextButton.inert = currentItemNumber === totalItemsCount;
        this.#prevButton.inert = currentItemNumber === 1;
      }
    } else {
      this.#nextButton!.inert =
        Math.ceil(
          this.#scrollContainer.scrollLeft + this.#scrollContainer.clientWidth,
        ) >= this.#scrollContainer.scrollWidth; // Prevent rounding issues in zoomed browsers
      this.#prevButton!.inert = this.#scrollContainer.scrollLeft === 0;
    }
  }

  #updateItemCounter(itemNumber?: string) {
    if (itemNumber) {
      this.#currentItem.textContent = itemNumber;
      this.#itemCounter.hidden = false;
    } else {
      this.#itemCounter.hidden = true;
    }
  }

  #updateDotIndicator(itemIndex?: string) {
    if (!itemIndex) return;
    this.#dots.activeDot = parseInt(itemIndex);
  }

  #updateItemClasses(itemIndex?: string) {
    if (!itemIndex) return;
    const current = parseInt(itemIndex);
    const items = this.#slots.items!.assignedElements()! as HTMLElement[];
    items.forEach((item, index) => {
      index++; // Should start at 1, not at 0
      if (index === current) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
  }

  #updateThumbnailClasses(itemNumber?: string) {
    const current = itemNumber ? parseInt(itemNumber) : null;
    this.#thumbnailItems.forEach((item, index) => {
      index++; // Should start at 1, not at 0
      if (index === current) {
        item.classList.add("active");
      } else {
        item.classList.remove("active");
      }
    });
  }

  #updateThumbnailPosition(itemNumber?: string) {
    if (!itemNumber) return;
    const current = parseInt(itemNumber);
    let thumbWidth = (this.#thumbnails.querySelector(".teaser")! as HTMLElement)
      .offsetWidth;
    if (window.innerWidth >= 768) {
      thumbWidth += 12; // Thumb + Gap
      this.#thumbnailScroller.scrollLeft =
        thumbWidth * current - thumbWidth * 2;
    } else {
      thumbWidth += 8; // Thumb + Gap
      this.#thumbnailScroller.scrollLeft =
        thumbWidth * current -
        this.#thumbnailScroller.offsetWidth / 2 -
        thumbWidth / 2 -
        4;
    }
  }

  #updateBrowserUrl(itemUrl?: string) {
    if (!itemUrl) return;
    const currentUrl = new URL(window.location.href);
    const updatedUrl = new URL(itemUrl);
    currentUrl.searchParams.forEach((value, key) => {
      updatedUrl.searchParams.set(key, value);
    });
    try {
      window.history.replaceState(null, "", updatedUrl.toString());
    } catch (error) {
      console.error("An unexpected error occurred:", error);
    }
  }

  disconnectedCallback() {
    if (this.#nextButton && !this.isRestricted) {
      this.#nextButton.removeEventListener("click", this.#scrollNext);
    }
    if (this.#prevButton && !this.isRestricted) {
      this.#prevButton.removeEventListener("click", this.#scrollPrevious);
    }
    if (this.#thumbnailItems && !this.isRestricted) {
      this.#thumbnailItems.forEach((item) => {
        item.removeEventListener("click", this.#handleThumbnailClick);
      });
    }
    this.#itemsObserver.disconnect();
    this.#interactionObserver?.disconnect();
    this.#mutationObserver.disconnect();

    if (this.isRestricted) {
      this.#scrollContainer.removeEventListener("wheel", this.#handleWheel);
      this.#scrollContainer.removeEventListener(
        "touchstart",
        this.#handleTouchStart,
      );
      this.#scrollContainer.removeEventListener(
        "touchmove",
        this.#handleTouchMove,
      );
    }
  }
}

"customElements" in window &&
  customElements.get("ws-gallery-lead") === undefined &&
  customElements.define("ws-gallery-lead", GalleryLead);

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface HTMLElementTagNameMap {
    "ws-gallery-lead": GalleryLead;
  }
}
